<template>
  <page-loader>
    <editor-toolbar
      :is-loading="isLoading"
      :item="partnerOriginal.partner.default"
      :item-updated="partnerUpdated.partner.default"
      :slug="partnerOriginal.slug"
      type="partner"
      :publish-chip-editable="true"
      :is-archived="isArchived"
    />
    <v-divider />
    <v-container>
      <v-tabs v-model="tab" background-color="transparent" class="mb-4">
        <v-tab :disabled="isPerformingAction">Partner Information</v-tab>
        <v-tab :disabled="isPerformingAction">Configuration</v-tab>
        <v-tab :disabled="isPerformingAction">Deals</v-tab>
        <v-tab :disabled="isPerformingAction">Linked Offers</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab" class="transparent" continuous>
        <v-tab-item>
          <geo-tabs
            v-model="tabGeo"
            :countries="countriesPredefined"
            :is-disabled="isArchived || !canPerformActions || !isPermittedTo('update')"
            @add="onAddCountry"
            @delete="onDeleteCountry"
          />
          <v-card class="pa-2" outlined>
            <v-container px-0>
              <v-row v-if="categorisedPartnerSchema.general['Imagery']">
                <v-col class="pt-0">
                  <category-options category="Imagery">
                    <v-row>
                      <v-col sm="4">
                        <select-image
                          v-model="partnerUpdatedByGeo.logoFeatured"
                          :is-disabled="isArchived || isDisabled"
                          :size="250"
                          :title="partnerUpdatedDefault.name"
                          label="Featured Logo (720x360)"
                          show-extra-previews
                        />
                      </v-col>
                      <v-col sm="4">
                        <select-image
                          v-model="partnerUpdatedByGeo.logoThumbnail"
                          :is-disabled="isArchived || isDisabled"
                          :size="250"
                          :title="partnerUpdatedDefault.name"
                          label="Thumbnail Logo (300x300)"
                        />
                      </v-col>
                      <v-col sm="4">
                        <select-image
                          v-model="partnerUpdatedByGeo.logoTransparent"
                          :is-disabled="isArchived || isDisabled"
                          :size="250"
                          :title="partnerUpdatedDefault.name"
                          label="Transparent Logo"
                          show-extra-previews
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="pt-0" sm="4">
                        <select-image
                          v-model="partnerUpdatedByGeo.logoFunnel"
                          :is-disabled="isArchived || isDisabled"
                          :title="partnerUpdatedDefault.name"
                          :size="250"
                          label="Funnel Logo"
                          show-extra-previews
                        />
                      </v-col>
                    </v-row>
                  </category-options>
                </v-col>
              </v-row>
              <v-row>
                <v-col v-if="categorisedPartnerSchema.general['General Information']" sm="8">
                  <category-options
                    category="General Information"
                    :data="partnerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="categorisedPartnerSchema.general['General Information']"
                    :is-disabled="isArchived || isDisabled"
                    :is-loading="isLoading"
                    :is-default="currentCountry === 'default'"
                    @input="onInput"
                  />
                </v-col>
                <v-col v-if="categorisedPartnerSchema.general['Features']" sm="4">
                  <category-options
                    category="Features"
                    :data="partnerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="categorisedPartnerSchema.general.Features"
                    :is-disabled="isArchived || isDisabled"
                    :is-loading="isLoading"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col v-if="categorisedPartnerSchema.general['Opening Times']">
                  <category-options
                    category="Opening Times"
                    :data="partnerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="categorisedPartnerSchema.general['Opening Times']"
                    :is-disabled="isArchived || isDisabled"
                    :is-loading="isLoading"
                    :is-accordion="true"
                    :is-default="currentCountry === 'default'"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col v-if="categorisedPartnerSchema.general['Lead Cap']">
                  <category-options
                    category="Lead Cap"
                    :data="partnerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="categorisedPartnerSchema.general['Lead Cap']"
                    :is-disabled="isArchived || isDisabled"
                    :is-loading="isLoading"
                    :is-accordion="true"
                    :is-default="currentCountry === 'default'"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col v-for="(fields, category) in categorisedSchemaRemaining" :key="category" sm="6">
                  <category-options
                    :category="category"
                    :data="partnerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="fields"
                    :is-disabled="isArchived || isDisabled"
                    :is-loading="isLoading"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
              <template v-if="Object.keys(categorisedPartnerSchema.language).length">
                <v-row>
                  <v-col>
                    <v-toolbar color="secondary" dark dense flat>
                      <v-toolbar-title class="d-flex align-center">
                        <v-icon class="mr-4">language</v-icon>
                        Language fields
                      </v-toolbar-title>
                      <v-spacer />
                      <v-select
                        v-model="query.params.language"
                        :disabled="isArchived || isLoading"
                        :items="languageOptions"
                        :loading="isLoading"
                        class="flex-grow-0 context-input"
                        item-text="key"
                        label="Select Language"
                        dense
                        hide-details
                        solo
                      />
                    </v-toolbar>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    v-for="(category, key) in categorisedPartnerSchema.language"
                    :key="`language-${category.key}`"
                    sm="6"
                  >
                    <category-options
                      :category="key"
                      :context="query.params.language"
                      :data="partnerUpdatedByGeo"
                      :exclude="hiddenProperties"
                      :fields="category"
                      :is-disabled="isArchived || isDisabled"
                      :is-loading="isLoading"
                      @input="onInput"
                    />
                  </v-col>
                </v-row>
              </template>
              <template v-if="Object.keys(categorisedPartnerSchema.currency).length">
                <v-row>
                  <v-col>
                    <v-toolbar color="secondary" dark dense flat>
                      <v-toolbar-title class="d-flex align-center">
                        <v-icon class="mr-4">attach_money</v-icon>
                        Currency fields
                      </v-toolbar-title>
                      <v-spacer />
                      <v-select
                        v-model="query.params.currency"
                        :disabled="isArchived || isLoading"
                        :items="getItemsForOption('mainCurrencies')"
                        :loading="isLoading"
                        class="flex-grow-0 context-input"
                        item-text="key"
                        label="Select Currency"
                        dense
                        hide-details
                        solo
                      />
                    </v-toolbar>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    v-for="(category, key) in categorisedPartnerSchema.currency"
                    :key="`currency-${category.key}`"
                    sm="6"
                  >
                    <category-options
                      :category="key"
                      :context="query.params.currency"
                      :data="partnerUpdatedByGeo"
                      :exclude="hiddenProperties"
                      :fields="category"
                      :is-disabled="isArchived || isDisabled"
                      :is-loading="isLoading"
                      @input="onInput"
                    />
                  </v-col>
                </v-row>
              </template>
            </v-container>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card class="pa-2" outlined>
            <v-row>
              <v-col v-if="categorisedPartnerSchema.general.Configuration">
                <category-options
                  category="Configuration"
                  :data="partnerUpdatedDefault"
                  :exclude="hiddenProperties"
                  :fields="categorisedPartnerSchema.general.Configuration"
                  :is-disabled="isArchived || isDisabled"
                  :is-loading="isLoading"
                  @input="onInputDefault"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-container>
            <v-row>
              <v-col>
                <v-card outlined>
                  <deals-table :deals="deals" :show-partner="false" />
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <offers-list
            :chips="['offer-type', 'published']"
            :is-loading="isLoading"
            :items="mappedLinkedOffers"
            link="offers"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-container>
    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      :is-published="partnerUpdatedDefault.published"
      :is-archived="isArchived"
      context="Partner"
      @delete="showDeleteModal = true"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
    />
    <save-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Changes"
      @modal-confirm="onSaveConfirm"
    >
      <div v-if="saveModalPublishMessage" class="subtitle-1 font-weight-bold text-uppercase orange--text mb-3">
        {{ saveModalPublishMessage }}
      </div>
      <diff-view :original="partnerOriginal.partner" :updated="partnerUpdated.partner" :schema="partnerSchema" />
    </save-modal>
    <delete-modal
      v-model="showDeleteModal"
      :item="partnerUpdatedByGeo.name || partnerUpdatedDefault.name"
      category="Partner"
      is-archivable
      @modal-confirm="onDeleteConfirm"
    />
    <reset-modal v-model="showResetModal" category="partner" @modal-confirm="onResetConfirm" />
    <exit-modal :value="showExitModal" @modal-confirm="confirmExitBeforeSave" @modal-cancel="cancelExitBeforeSave" />
  </page-loader>
</template>

<script>
// Helpers
import Vue from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
// Components
import FabActions from '@/components/fab-actions'
import DeleteModal from '@/components/modals/delete-modal'
import ResetModal from '@/components/modals/reset-modal'
import SaveModal from '@/components/modals/save-modal'
import DiffView from '@/components/object-diff/diff-view'
import EditorToolbar from '@/components/editor-toolbar'
import GeoTabs from '@/components/geo-tabs'
import PageLoader from '@/components/page-loader'
import SelectImage from '@/components/forms/select-image'

import DealsTable from '@/views/deals/components/deals-table'
import OffersList from '@/views/offers/components/offers-list'
import CategoryOptions from '@/components/category-options'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { deals, languages, offers, partners } from '@/services/offers'

export default {
  name: 'PartnerEditor',
  components: {
    FabActions,
    CategoryOptions,
    DealsTable,
    DeleteModal,
    DiffView,
    EditorToolbar,
    GeoTabs,
    OffersList,
    PageLoader,
    ResetModal,
    SaveModal,
    SelectImage,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    hasErrors: false,
    isLoading: false,
    isDrafting: false,
    isPublishing: false,
    isArchived: false,
    queriesDefined: ['currency', 'language'],
    queryDefaults: ['currency', 'language'],
    hiddenProperties: ['published'],
    languageOptions: [],
    linkedOffers: [],
    offers: [],
    deals: [],
    partnerOriginal: { partner: { default: {} } },
    partnerUpdated: { partner: { default: {} } },
    tab: 0,
    tabGeo: 0,
    showDeleteModal: false,
    showResetModal: false,
    showSaveModal: false,
    showLinkModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['getItemsForOption']),
    ...mapGetters('partners', ['categorisedPartnerSchema', 'partnerSchema']),
    countriesPredefined() {
      return Object.keys(this.partnerUpdated.partner).sort(country => (country === 'default' ? -1 : 0))
    },
    currentCountry() {
      return this.countriesPredefined[this.tabGeo]
    },
    currentCountryFormatted() {
      return this.formatDiffKey('countries', this.currentCountry)
    },
    partnerUpdatedDefault() {
      return this.partnerUpdated.partner.default
    },
    partnerUpdatedByGeo() {
      return this.partnerUpdated.partner[this.currentCountry]
    },
    categorisedSchemaRemaining() {
      const ignore = ['Features', 'General Information', 'Imagery', 'Configuration', 'Opening Times', 'Lead Cap']
      return Object.entries(this.categorisedPartnerSchema.general).reduce((acc, [key, data]) => {
        return ignore.includes(key) ? acc : { ...acc, [key]: data }
      }, {})
    },
    hasChanges() {
      return this.hasDataChanged(this.partnerOriginal, this.partnerUpdated)
    },
    isPerformingAction() {
      return this.isLoading || this.hasPageErrors
    },
    isDisabled() {
      return this.isPerformingAction || !this.isPermittedTo('update')
    },
    mappedLinkedOffers() {
      return this.offers.filter(({ slug }) => this.linkedOffers.find(({ slug: linkedSlug }) => linkedSlug === slug))
    },
    saveModalPublishMessage() {
      if (this.partnerUpdated.partner.default.published !== this.partnerOriginal.partner.default.published) {
        return this.partnerUpdated.partner.default.published
          ? 'You are publishing this partner'
          : 'You are putting this partner into draft'
      }
      return false
    },
  },
  async created() {
    this.setBeforeUnloadListener(this.onBeforeUnload)
    await Promise.all([this.fetchLanguageOptions(), this.getPartnerData()])
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapActions('partners', ['getPartnerSchema']),
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async fetchLanguageOptions() {
      this.isLoading = true
      try {
        this.languageOptions = (await languages.getLanguages()).map(({ name, code }) => ({ key: name, value: code }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translation language options',
          duration: -1,
          data: { error },
        })
      }
      this.isLoading = false
    },
    async getPartnerData() {
      try {
        const [partnerData, linkedOffers, dealData, allOffers] = await Promise.all([
          partners.getSinglePartner(this.$route.params.slug),
          partners.getOffersForPartner(this.$route.params.slug),
          deals.getDealsForPartner(this.$route.params.slug),
          offers.getAllOffers({ compact: true }),
        ])
        this.partnerOriginal = this.clone(partnerData)
        this.partnerUpdated = this.clone(partnerData)
        this.linkedOffers = linkedOffers
        this.offers = allOffers.data
        this.deals = dealData.data
        this.isArchived = Boolean(partnerData.archived)
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch partner data',
        })
      }
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.partnerUpdatedByGeo[model]) {
          Vue.set(this.partnerUpdatedByGeo[model], context, value)
        } else {
          Vue.set(this.partnerUpdatedByGeo, model, { [context]: value })
        }
      } else Vue.set(this.partnerUpdatedByGeo, model, value)
    },
    onInputDefault({ context, model, value }) {
      if (context) {
        if (this.partnerUpdatedDefault[model]) {
          Vue.set(this.partnerUpdatedDefault[model], context, value)
        } else {
          Vue.set(this.partnerUpdatedDefault, model, { [context]: value })
        }
      } else Vue.set(this.partnerUpdatedDefault, model, value)
    },
    onResetConfirm() {
      this.partnerUpdated = this.clone(this.partnerOriginal)
      this.$notify({
        type: 'success',
        text: `Partner "${this.partnerUpdatedDefault.name}" reset successfully`,
      })
      this.showResetModal = false
    },
    async onSaveConfirm() {
      this.showSaveModal = false
      this.isLoading = true

      try {
        await partners.saveSinglePartner(this.partnerOriginal.slug, this.partnerUpdated.partner)
        this.$notify({
          type: 'success',
          text: `Partner "${this.partnerUpdatedDefault.name}" saved successfully`,
        })
        await this.getPartnerData()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save partner',
        })
      }

      this.isLoading = false
    },
    async onDeleteConfirm() {
      this.isLoading = true

      try {
        await partners.deletePartner(this.partnerOriginal.slug)
        this.$notify({
          type: 'success',
          text: `Partner "${this.partnerUpdatedDefault.name}" deleted successfully`,
        })
        this.$router.push({ name: 'partners.management' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete partner',
        })
      }

      this.isLoading = false
    },
    onAddCountry(country) {
      Vue.set(this.partnerUpdated.partner, country, {})
    },
    onDeleteCountry(country) {
      Vue.delete(this.partnerUpdated.partner, country)
    },
  },
}
</script>

<style lang="scss" scoped>
.context-input {
  max-width: 200px;
}
</style>
